// TIPSSSO role taxonomy (spec 3.6 + 4.4).
//
// Roles are defined in ZITADEL projects and granted to orgs/users. This file is the
// canonical TypeScript mirror used for compile-time-safe authorization checks. The
// full taxonomy lives in ALL_ROLES; the Roles map exposes the commonly referenced
// ones by name.

export const Roles = {
  // platform.*
  PlatformSuperAdmin: 'platform.super_admin',
  PlatformSecurityAdmin: 'platform.security_admin',
  PlatformOrgAdmin: 'platform.org_admin',
  PlatformAuditViewer: 'platform.audit_viewer',
  PlatformBillingAdmin: 'platform.billing_admin',
  PlatformProvisioningAdmin: 'platform.provisioning_admin',

  // sales.*
  SalesAccountOwner: 'sales.account_owner',
  SalesResellerManager: 'sales.reseller_manager',
  SalesSdr: 'sales.sdr',
  SalesAe: 'sales.ae',
  SalesManager: 'sales.manager',
  SalesOpsAdmin: 'sales.ops_admin',

  // support.*
  SupportRep: 'support.rep',
  SupportManager: 'support.manager',
  SupportImpersonationAllowed: 'support.impersonation_allowed',
  SupportAuditViewer: 'support.audit_viewer',

  // reseller.*
  ResellerOwner: 'reseller.owner',
  ResellerAdmin: 'reseller.admin',
  ResellerSalesManager: 'reseller.sales_manager',
  ResellerSalesRep: 'reseller.sales_rep',
  ResellerSupportRep: 'reseller.support_rep',
  ResellerBillingAdmin: 'reseller.billing_admin',
  ResellerCustomerAdmin: 'reseller.customer_admin',

  // customer.*
  CustomerOwner: 'customer.owner',
  CustomerAdmin: 'customer.admin',
  CustomerUser: 'customer.user',
  CustomerBillingAdmin: 'customer.billing_admin',
  CustomerAuditViewer: 'customer.audit_viewer',

  // tipsdata.*
  TipsDataQueryUser: 'tipsdata.query_user',
  TipsDataQueryAdmin: 'tipsdata.query_admin',
  TipsDataExportUser: 'tipsdata.export_user',
  TipsDataApiUser: 'tipsdata.api_user',

  // databaseemailer.*
  DatabaseEmailerQueryUser: 'databaseemailer.query_user',
  DatabaseEmailerQueryAdmin: 'databaseemailer.query_admin',
  DatabaseEmailerResellerAdmin: 'databaseemailer.reseller_admin',

  // tipsoutreach.*
  OutreachSender: 'tipsoutreach.sender',
  OutreachCampaignAdmin: 'tipsoutreach.campaign_admin',
  OutreachDomainAdmin: 'tipsoutreach.domain_admin',
  MicrosoftConsentAdmin: 'tipsoutreach.microsoft_consent_admin',

  // tipsverify.*
  TipsVerifyReviewer: 'tipsverify.reviewer',
  TipsVerifyAdmin: 'tipsverify.admin',

  // tipsconnect.*
  TipsConnectAdmin: 'tipsconnect.admin',
  TipsConnectSalesOpsUser: 'tipsconnect.salesops_user',
  TipsConnectResellerManager: 'tipsconnect.reseller_manager',
  TipsConnectSupportUser: 'tipsconnect.support_user',

  // tipscockpit.*
  TipsCockpitOperator: 'tipscockpit.operator',
  TipsCockpitAdmin: 'tipscockpit.admin',

  // tipscontact.* (CRM contact-center — softphone/dialing + chat)
  TipsContactAgent: 'tipscontact.agent',
  TipsContactAdmin: 'tipscontact.admin',

  // tipstrack.* (ATS — SDR hiring pipeline w/ Indeed sync)
  TipsTrackAdmin: 'tipstrack.admin',
  TipsTrackRecruiter: 'tipstrack.recruiter',
  TipsTrackViewer: 'tipstrack.viewer',

  // tipsairflow.* (project — Airflow; mapped to FAB roles in airflow-home image/webserver_config.py)
  AirflowAdmin: 'tipsairflow.admin',
  AirflowOp: 'tipsairflow.op',
  AirflowUser: 'tipsairflow.user',
  AirflowViewer: 'tipsairflow.viewer',

  // analytics.*
  AggregateQueryUser: 'analytics.aggregate_query_user',
  AggregateQueryAdmin: 'analytics.aggregate_query_admin',
  ResellerUsageViewer: 'analytics.reseller_usage_viewer',
  CustomerUsageViewer: 'analytics.customer_usage_viewer',
  ExportAggregateReport: 'analytics.export_aggregate_report',
} as const;

export type Role = (typeof Roles)[keyof typeof Roles];

export const ALL_ROLES: readonly string[] = Object.values(Roles);

/**
 * Roles that SCIM group mappings and reseller delegation may NEVER assign.
 * Enforced in processScimEvent and the provisioning grant paths.
 */
export const DANGEROUS_ROLES: ReadonlySet<string> = new Set<string>([
  Roles.PlatformSuperAdmin,
  Roles.PlatformSecurityAdmin,
  Roles.PlatformOrgAdmin,
  Roles.PlatformProvisioningAdmin,
  Roles.PlatformBillingAdmin,
  Roles.SupportImpersonationAllowed,
]);

/** Roles permitted to run aggregate ClickHouse queries (spec 4.6). */
export const AGGREGATE_QUERY_ROLES: ReadonlySet<string> = new Set<string>([
  Roles.ResellerSalesRep,
  Roles.ResellerSalesManager,
  Roles.ResellerAdmin,
  Roles.AggregateQueryUser,
  Roles.AggregateQueryAdmin,
]);
